import * as React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { CHESS_API, type Chess } from '@chess/api-chess';
import { REVIEW_ROUTE, type ReviewRouteParams } from '@chess/api-games';
import { PHASES_API, REVIEW_API, createWorkspace, type PhasesProvider, type ReviewGame } from '@chess/api-review';
import { ANALYSIS_API, type Analysis } from '@chess/api-analysis';
import { LOG_API, type Log } from '@chess/api-log';
import { OPENINGS_API, type Openings } from '@chess/api-openings';
import { CONSTRAINTS_API, type ConstraintRegistry } from '@chess/api-constraints';
import { plyOf } from './ply-fragment.ts';
import { NO_WRITES, onLocation, onState, type AddressSync } from './address.ts';
import { createPhasesOf, reviewGameOf } from './loader.ts';
import { MISTAKE_RULES_API, STORE_API, type GameStore, type MistakeRulesSource } from '@chess/api-model';

// The review screen: v1's three bands as the `review` layout, with each
// region bound to this screen's slots. The screen names no panel. It
// provides the `review` interface: which game is open, read from the route
// `/review/{website}/{username}/{date}/{id}` (only the id is looked up; the
// rest makes a shared address self-describing), and the ply the reader is on.
const bind = {
  header: 'review.header',
  left: 'review.moveList',
  // Stepping is the most common interaction, so the controls sit under the board.
  center: ['review.boardTabs', 'review.board', 'review.stepControls'],
  // The engine's shortlist sits above the learning tabs, visible whichever is open.
  sideTop: 'review.shortlist',
  right: 'review.sidePanel',
};

export function setup(app: PiletApi) {
  // A game is read from storage through the game store and parsed by the chess API, which
  // also plays the reader's own moves when they leave the game.
  let chessApi: Chess | undefined;
  void app.getApi<Chess>(CHESS_API).then((c) => (chessApi = c));

  // Where the opening ends and the endgame begins (loader.ts): the `phases`
  // provider with the book, or the rules without one when it is absent or slow.
  const phasesOf = createPhasesOf({
    provider: () => app.getApi<PhasesProvider>(PHASES_API),
    warn: (message, data) => log?.warn(message, data),
  });

  // The workspace: board tabs, the first pinned to the game the address names,
  // more opened beside it to compare. As the `review` API it stands in for
  // whichever tab shows, so the panels follow the active tab unchanged.
  const review = createWorkspace(
    async (id): Promise<ReviewGame | undefined> => {
      const [store, chess] = await Promise.all([app.getApi<GameStore>(STORE_API), app.getApi<Chess>(CHESS_API)]);
      chessApi = chess;
      return reviewGameOf(id, await store.record(id), chess, phasesOf);
    },
    { play: (fen, move) => chessApi?.play(fen, move) },
  );
  app.registerApi(REVIEW_API, review);
  // The session loads each game's scan and opening book once, for every
  // panel, as soon as the two APIs are there; a build without the book still
  // works, with theory only at the start.
  void app.getApi<Analysis>(ANALYSIS_API).then((analysis) => review.connect({ analysis }));
  void app.getApi<Openings>(OPENINGS_API).then((openings) => review.connect({ openings }));
  // Blunders and misses follow the user's rules (settings); a change re-reads
  // the open scans' verdicts. Without the settings plugin, the defaults.
  void app.getApi<MistakeRulesSource>(MISTAKE_RULES_API).then(async (mistakeRules) => {
    await mistakeRules.ready();
    review.connect({ mistakeRules });
  });
  // Every board enforces its own scope's Controls and Next move constraints.
  void app.getApi<ConstraintRegistry>(CONSTRAINTS_API).then((constraints) => review.connect({ constraints }));
  let log: Log | undefined;
  void app.getApi<Log>(LOG_API).then((l) => (log = l.child(app.meta.name)));

  // Opening a game starts its scan, so the arrows, the shortlist and later
  // the lessons have the engine's reading of every position. A complete scan
  // is never redone; a half one continues.
  const scanOnOpen = (gameId: string): void => {
    void app
      .getApi<Analysis>(ANALYSIS_API)
      .then((analysis) => analysis.scan(gameId))
      .catch((error) => log?.warn('scan did not complete', { gameId, error: String(error) }));
  };

  // `#ply=N` on the address opens the game at that ply: how another plugin
  // sends the reader to a position, in this game or another, without the
  // screen unmounting.

  const Screen: React.FC = () => {
    const { id } = useParams<Partial<ReviewRouteParams>>();
    const { hash, key } = useLocation();
    const navigate = useNavigate();
    // The address and the board, one rule (address.ts): the fragments the
    // review wrote and still waits to see back, and whether this is the
    // first location the screen sees.
    const sync = React.useRef<AddressSync>(NO_WRITES);
    const seen = React.useRef(false);
    const gameId = id ? decodeURIComponent(id) : undefined;
    const ply = plyOf(hash);
    // Until the ply the address asked for has been applied, the board's ply
    // must not be written back, or the opening position would overwrite it.
    const settled = React.useRef(false);
    React.useEffect(() => {
      settled.current = false;
      void review.open(gameId).then(() => {
        if (ply !== undefined && review.state().game?.id === gameId) review.go(ply);
        settled.current = true;
      });
      if (gameId) scanOnOpen(gameId);
    }, [gameId]);
    // A location reached (every one has a new key, the review's own replaces
    // included): the echo of a write is nothing; anything else is the
    // reader's request, and the board goes to the ply it names, from a line
    // or a look back as well.
    React.useEffect(() => {
      const first = !seen.current;
      seen.current = true;
      const { action, sync: next } = onLocation(sync.current, { hash, first, state: review.state(), gameId });
      sync.current = next;
      if (action.kind === 'go') review.go(action.ply);
    }, [hash, key]);
    // The address follows the board: each step along the game replaces the
    // `#ply=N` fragment (no fragment at the starting position), so a reload or
    // a shared link opens the same position. Replaced, not pushed, so the
    // browser's back button leaves the game rather than undoing moves. A line
    // of the reader's own, or a puzzle, keeps the game ply it branches from.
    React.useEffect(
      () =>
        review.subscribe((state) => {
          const { write, sync: next } = onState(sync.current, { state, gameId, settled: settled.current, hash: window.location.hash });
          sync.current = next;
          if (write === undefined) return;
          navigate({ pathname: window.location.pathname, search: window.location.search, hash: write }, { replace: true, preventScrollReset: true });
        }),
      [gameId],
    );
    return <app.Extension name="layout" params={{ layout: 'review', bind }} />;
  };

  app.registerPage(REVIEW_ROUTE, Screen);
  app.registerPage('/review', Screen);
}

